import React from 'react';
import styled from 'styled-components';

import { P, PProps } from '@decisiv/ui-components/lib/components/Typography';

import OverflowPopover from 'components/OverflowPopover';

import useTruncationReveal from 'hooks/useTruncationReveal';

type Props = PProps & {
  /**
   * The plain-text value. Shown in the popover, and rendered as the line
   * itself unless `children` provides a decorated version of it.
   */
  text?: string | number | null;
  /**
   * Rendered form of `text` — e.g. the same value with the search term
   * highlighted. Must be inline content: `text-overflow` produces an ellipsis
   * only for a block's own inline text, so a nested block or flex box here
   * would be clipped with no ellipsis at all. For text that has to live inside
   * a KDS control, clamp it with `ClampedLabel` and drive the reveal from
   * `hooks/useTruncationReveal` on the control instead.
   */
  children?: React.ReactNode;
  /** Set by `styled(TruncatedText)`, so callers can add their own rules. */
  className?: string;
  'data-testid'?: string;
};

// `width: 100%` rather than `max-width` alone: as a flex item the line would
// otherwise be sized from its content and only clamp if flex-shrink happened
// to kick in, which silently skips the ellipsis in a cell that has room.
const ClampedText = styled(P).attrs({ truncate: true })`
  display: block;
  width: 100%;
  min-width: 0;
  max-width: 100%;
` as typeof P;

/**
 * The clamp on its own, for text that has to sit inside a KDS `Link`, `Button`
 * or `Tag`. Those render as flex containers, so clamping the block around them
 * clips the whole control without an ellipsis and leaves nothing overflowing
 * to measure; clamping this span inside them puts the ellipsis on the text and
 * leaves any trailing icon in place.
 */
export const ClampedLabel = styled.span`
  display: block;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * A single line of cell text that truncates with an ellipsis at the column's
 * width and reveals the full value in a popover on hover.
 *
 * The reveal is only armed once the text actually overflows, so a value that
 * fits does not sprout a panel repeating what is already on screen.
 */
export default function TruncatedText({
  text,
  children,
  ...typography
}: Props) {
  const { measureRef, isRevealed, revealProps, conceal } =
    useTruncationReveal<HTMLParagraphElement>();

  const fullValue = text == null ? '' : String(text);

  return (
    <>
      <ClampedText {...typography} {...revealProps} ref={measureRef}>
        {children ?? text}
      </ClampedText>
      {!!fullValue && isRevealed && (
        <OverflowPopover target={measureRef} onRequestHide={conceal}>
          {fullValue}
        </OverflowPopover>
      )}
    </>
  );
}
